import * as React from 'react';
import noop from 'lodash/noop';
import {emptyArray} from '@packages/utilities/fp/utils';
import {append} from '@packages/utilities/array';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {styledDiv, emotionStyled, tokens} from '@packages/utilities/styled';
import {
  Expressions,
  TypeImplementation,
  VariableMap,
  ExpressionPath,
  ExpressionPathPart,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  ExpressionEditorImplementationContext,
  extendExpressionEditorActionAtPath,
  isImplicitFunction,
  useExpressionEditorImplementationContext,
  type ExpressionEditorDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import Button from '@packages/systems/design-system/Button';
import {CustomizeIcon} from '@packages/systems/spring';
import {BINDING_SELECT_BUTTON_HEIGHT} from './constants';

const Wrapper = emotionStyled('div')({
  padding: tokens.space[2],
  width: rightSidebarWidth,
});

const Divider = styledDiv(({theme}) => ({
  height: 1,
  backgroundColor: theme.colors.backgroundDeep,
  width: `calc(100% + ${theme.gutters.x8 * 2}px)`,
  marginLeft: -theme.gutters.x8,
  marginBottom: theme.gutters.normal,
}));

const divider = <Divider />;

const Separator = emotionStyled('div')({
  height: tokens.space[2],
});

const separator = <Separator />;

const Label = styledDiv(
  ({
    theme: {
      typography: {subheading},
      control,
    },
  }) => ({
    fontWeight: subheading.fontWeight,
    lineHeight: `${control.height + 2 * control.borderWidth}px`,
    fontSize: subheading.fontSize,
  })
);

const atArg: ExpressionPathPart = {in: 'Call', at: 'arg'};
const atFun: ExpressionPathPart = {in: 'Call', at: 'fun'};

type ArgsType = Array<{
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}>;

const ArgumentSettingsButton = emotionStyled(Button)({
  height: `${BINDING_SELECT_BUTTON_HEIGHT}px !important`,
  width: `${BINDING_SELECT_BUTTON_HEIGHT}px !important`,
  marginLeft: '$1',
});

const renderArgumentSettingsButton = (
  ref: React.RefObject<any>,
  openPopover: (arg1: unknown) => void
) => (
  <ArgumentSettingsButton
    _ref={ref}
    data-automation-id="call-argument-settings-ghost-button"
    dynamic
    onClick={openPopover}
    structure="ghost"
  >
    <CustomizeIcon />
  </ArgumentSettingsButton>
);

/**
 * Content of the argument settings popover.
 */
const ArgumentSettingsContent = ({
  label,
  finalArgs,
}: {
  label: string;
  finalArgs: ArgsType;
}) => {
  const {env} = useExpressionEditorImplementationContext();
  // Argument settings are nested fields that never get the parent context's
  // top-level override label row, so always render the arguments in the
  // `default` context to keep their labels (e.g. "Open in" / "Preload").
  const argumentEnv: TypeImplementation.DataTypeEnvironment =
    env.context === 'default' ? env : {...env, context: 'default'};

  return (
    <ExpressionEditorImplementationContext.Provider value={{env: argumentEnv}}>
      <Wrapper>
        <Label>{label}</Label>
        {separator}
        {finalArgs.map((arg, index) => (
          <React.Fragment key={index}>
            {index > 0 ? (
              <>
                {separator}
                {divider}
              </>
            ) : null}
            <ExpressionEditor dispatch={arg.dispatch} expression={arg.expr} />
          </React.Fragment>
        ))}
      </Wrapper>
    </ExpressionEditorImplementationContext.Provider>
  );
};

export const ArgumentSettings = ({
  fun,
  label,
  args,
  children,
  dispatch,
  onClose = noop,
}: {
  fun: ContextualExpression;
  label: string;
  args: Array<ContextualExpression>;
  children?: (
    ref: React.RefObject<any>,
    arg1: (arg2?: unknown) => void
  ) => React.ReactElement<any>;
  dispatch: ExpressionEditorDispatch;
  onClose?: () => void;
}) => {
  const fnVariable = Expressions.getVariable(stripAll(fun));
  const fnValue = fnVariable
    ? VariableMap.get(getAnn(fun).env.context.values, fnVariable)
    : null;

  const builtArgs = buildArgumentsList(args, dispatch);

  // If we are rendering the arguments of an implicit function we don't
  // want to render the last argument, which is the implicitly bound value.
  const finalArgs = isImplicitFunction(fnValue)
    ? builtArgs.slice(0, -1)
    : builtArgs;

  if (finalArgs.length < 1) return null;

  const renderPopoverContent = () => (
    <ArgumentSettingsContent label={label} finalArgs={finalArgs} />
  );

  const argumentSettingsButton = children || renderArgumentSettingsButton;

  return (
    <DeprecatedPopoverBridge
      onChange={(newState) => (!newState.open ? onClose() : undefined)}
      renderPopoverContent={renderPopoverContent}
      showMask="TRANSPARENT"
      metricsIdentifier="argument-settings-popover"
    >
      {({Anchor, openPopover}) => (
        <Anchor asChild={false}>
          {(ref) => argumentSettingsButton(ref, openPopover)}
        </Anchor>
      )}
    </DeprecatedPopoverBridge>
  );
};

// This logic bundles the argument expressions with a dispatch function
// that points to that specific argument, this allows us to not render
// some of the arguments and still have correct dispatch handling for
// the rest
export const buildArgumentsList = (
  args: Array<ContextualExpression>,
  dispatch: ExpressionEditorDispatch
): ArgsType => {
  return args.reduceRight(
    (
      acc: {
        path: ExpressionPath;
        args: ArgsType;
      },
      curr
    ) => {
      const argPath = append(acc.path, atArg);
      acc.args.unshift({
        expr: curr,
        dispatch: extendExpressionEditorActionAtPath(dispatch)(argPath),
      });
      acc.path = append(acc.path, atFun);
      return acc;
    },
    {path: emptyArray, args: []}
  ).args;
};
